/**
 * @file components/chat/AttachmentPreview.tsx
 * CHAT-03 — Aperçu d'une pièce jointe conversationnelle dans le composer.
 *
 * Composant display pur : aucune logique d'upload, aucun effet de bord.
 * - Image  → thumbnail (previewUrl) ou icône Image
 * - PDF    → icône FileText + nom + taille
 * - Texte  → icône File + nom + taille
 * - Upload en cours → spinner à la place du bouton × (pas de suppression mid-upload)
 */
import React from 'react';
import { File, FileText, Image, Loader2, X } from 'lucide-react';

export interface AttachmentPreviewProps {
  filename: string;
  /** Taille en octets (affichée en Ko/Mo). */
  size: number;
  mimeType: string;
  /** URL object (revokeObjectURL à la suppression) pour les images uniquement. */
  previewUrl?: string;
  /** True pendant l'upload — remplace la croix par un spinner. */
  uploading?: boolean;
  onRemove: () => void;
}

function humanFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function FileIcon({ mimeType }: { mimeType: string }): React.ReactElement {
  if (mimeType === 'application/pdf')
    return <FileText size={20} className="shrink-0 text-red-400" />;
  if (mimeType.startsWith('image/'))
    return <Image size={20} className="shrink-0 text-blue-400" />;
  return <File size={20} className="shrink-0 text-muted-foreground" />;
}

export default function AttachmentPreview({
  filename,
  size,
  mimeType,
  previewUrl,
  uploading = false,
  onRemove,
}: AttachmentPreviewProps): React.ReactElement {
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-sm">
      {/* Thumbnail image ou icône type */}
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={filename}
          className="h-10 w-10 shrink-0 rounded object-cover"
        />
      ) : (
        <FileIcon mimeType={mimeType} />
      )}

      {/* Nom + taille */}
      <div className="min-w-0">
        <p className="max-w-[180px] truncate font-medium text-foreground">{filename}</p>
        <p className="text-[11px] text-muted-foreground">{humanFileSize(size)}</p>
      </div>

      {/* Spinner pendant l'upload, croix de suppression sinon */}
      {uploading ? (
        <Loader2 size={16} className="shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <button
          type="button"
          aria-label="Supprimer la pièce jointe"
          onClick={onRemove}
          className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
